// @ts-strict-ignore
import { NgIf, NgStyle } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  Output,
  type OnChanges,
  type OnInit,
  type SimpleChanges
} from '@angular/core';

import { hasChanged } from 'src/app/utils/functions/change-helpers';
import type { CssRules } from 'src/app/utils/models/css-rules.interface';
import { PERCENT_MAX } from 'src/app/utils/number-constants';

import { ImageState } from './image-state.enum';

@Component({
  selector: 'cc-image',
  templateUrl: './image.component.html',
  styleUrl: './image.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIf, NgStyle],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ImageComponent implements OnInit, OnChanges {
  @Input() public src: string;
  @Input() public sizes: string;
  @Input() public altText: string;
  @Input() public customStyles: object | undefined;
  @Input() public background: string | undefined;
  @Input() public overridePaddingBottom: string | undefined;

  @Input() public forceLoad = false;
  @Input() public isFullScreen = false;
  @Input() public hasShadow = false;
  @Input() public hasOpacity = false;
  @Input() public ratio = 0;

  @Input() public fixedHeight = false;

  @Output() public readonly loaded = new EventEmitter<Event>();
  @Output() public readonly failed = new EventEmitter<[Event, string]>();

  public state: ImageState;
  public readonly imageState = ImageState;

  public hasRatio = this.ratio !== 0;

  public get ratioStyles(): CssRules {
    if (this.ratio === 0) {
      return {};
    }
    const paddingBottom = this.overridePaddingBottom ?? `${PERCENT_MAX / this.ratio}%`;
    return { paddingBottom };
  }

  public ngOnChanges({ src, srcset, ratio }: SimpleChanges): void {
    if (hasChanged(src) || hasChanged(srcset)) this.state = ImageState.Loading;
    if (hasChanged(ratio)) this.hasRatio = this.ratio !== 0;
  }

  public ngOnInit(): void {
    this.state = ImageState.Loading;
  }

  public onImageLoaded(event: Event): void {
    if (event) {
      this.state = ImageState.OK;
      this.loaded.emit(event);
    }
  }

  public onImageError(event: Event, srcOrSrcset: string): void {
    this.state = ImageState.ImageError;
    this.failed.emit([event, srcOrSrcset]);
  }
}
